import { useCallback, useEffect, useRef, useState } from 'react';
import { askAssistant, sendAssistantFeedback } from '../../api/assistant';
import { rememberAssistantSession } from './assistantSessions';

// The API enforces both limits; the front mirrors the per-page one so the
// input is disabled before a refused request.
export const EXCHANGES_PER_SESSION = 5;

const errorKey = code =>
  ({
    ASSISTANT_SESSION_LIMIT: 'sessionLimit',
    ASSISTANT_QUOTA_EXCEEDED: 'quotaReached',
  })[code] ?? 'unavailable';

/**
 * KD-3848 — one page session with the assistant: the questions asked, their
 * streamed answers and ratings. Lives as long as the page, so closing the
 * panel keeps the conversation.
 */
export default function useAssistantChat({
  productType,
  productId,
  context,
  locale,
}) {
  const [sessionId, setSessionId] = useState(() => crypto.randomUUID());
  const [messages, setMessages] = useState([]);
  const [quotaReached, setQuotaReached] = useState(false);
  const controller = useRef(null);

  useEffect(() => () => controller.current?.abort(), []);

  const update = (id, patch) =>
    setMessages(list =>
      list.map(message =>
        message.id === id
          ? {
              ...message,
              ...(typeof patch === 'function' ? patch(message) : patch),
            }
          : message,
      ),
    );

  const ask = async question => {
    rememberAssistantSession(productType, productId, sessionId);
    const id = crypto.randomUUID();
    setMessages(list => [
      ...list,
      { id, question, answer: '', status: 'streaming' },
    ]);
    const abort = new AbortController();
    controller.current = abort;
    try {
      const events = askAssistant({
        productType,
        productId,
        body: { question, sessionId, context, locale },
        signal: abort.signal,
      });
      for await (const event of events) {
        if (event.type === 'text') {
          update(id, message => ({ answer: message.answer + event.text }));
        } else if (event.type === 'not_provided') {
          update(id, { notProvided: true });
        } else if (event.type === 'sources') {
          update(id, { sources: event.sources });
        } else if (event.type === 'done') {
          update(id, {
            status: 'done',
            exchangeId: event.exchangeId,
            notProvided: event.notProvided,
          });
        } else if (event.type === 'error') {
          update(id, { status: 'error', error: 'unavailable' });
        }
      }
      update(id, message =>
        message.status === 'streaming'
          ? { status: 'error', error: 'unavailable' }
          : {},
      );
    } catch (error) {
      if (abort.signal.aborted) return;
      if (error.code === 'ASSISTANT_QUOTA_EXCEEDED') setQuotaReached(true);
      update(id, { status: 'error', error: errorKey(error.code) });
    }
  };

  // reason: why an answer was rated down (wrong, on_page, incomplete, other).
  const rate = async (message, feedback, reason) => {
    update(message.id, { feedback });
    try {
      await sendAssistantFeedback(message.exchangeId, feedback, reason);
    } catch {
      update(message.id, { feedback: message.feedback });
    }
  };

  const reset = useCallback(() => {
    controller.current?.abort();
    setMessages([]);
    setQuotaReached(false);
    setSessionId(crypto.randomUUID());
  }, []);

  const sessionFull = messages.length >= EXCHANGES_PER_SESSION;
  const streaming = messages.some(message => message.status === 'streaming');

  return {
    sessionId,
    messages,
    ask,
    rate,
    reset,
    sessionFull,
    quotaReached,
    streaming,
    blocked: streaming || sessionFull || quotaReached,
  };
}
